import { Country_Enum, type GetLibraryCountryDatasourcesQuery } from "@/graphql";
import type { FilterSelection } from "./libraryMaterialFilterPayload";

// Pure helpers for the Library "Allowed EPDs" country filter. Kept free of the
// Apollo client so they can be unit-tested without the GraphQL/WebSocket runtime.

export type CountryDatasourceMap = Partial<Record<Country_Enum, string[]>>;

// Pure join of the two Hasura tables into a country -> distinct datasourceIds map.
// calculation_type_epd_datasource only carries calculationTypeId + epdDatasourceId,
// so the country is resolved via calculation_type (calculationTypeId -> country).
// Countries that end up with no datasource are dropped, so empty buckets never
// surface as sidebar flags.
export const buildCountryDatasourceMap = (data: GetLibraryCountryDatasourcesQuery | null | undefined): CountryDatasourceMap => {
  if (!data) return {};

  const countryByCalcType = new Map<number, Country_Enum>();
  for (const calculationType of data.calculationType ?? []) {
    // Skip the catch-all "Custom" calculation type: it allows the entire datasource
    // catalogue regardless of country, so every country owns one and it collapses
    // all per-country sets to the full catalogue. Excluding it lets the national
    // methods (BR-18, QNG, TEK17, ...) drive a country-specific datasource set.
    if (calculationType.label?.trim().toLowerCase() === "custom") continue;
    countryByCalcType.set(calculationType.id, calculationType.country);
  }

  const idsByCountry: Partial<Record<Country_Enum, Set<string>>> = {};
  for (const row of data.calculationTypeEpdDatasource ?? []) {
    const country = countryByCalcType.get(row.calculationTypeId);
    if (!country || !row.epdDatasourceId) continue;
    (idsByCountry[country] ??= new Set<string>()).add(String(row.epdDatasourceId));
  }

  const map: CountryDatasourceMap = {};
  for (const country of Object.keys(idsByCountry) as Country_Enum[]) {
    const ids = idsByCountry[country];
    if (ids && ids.size > 0) map[country] = [...ids];
  }
  return map;
};

// Translates a country -> epdDatasourceId (uuid) map into a country -> datasource
// NAME map, using an id->name lookup built from the api/DataSource list. The
// Library material search filters datasources by NAME — that is what the toolbar
// filter sends and what the backend matches (Material.datasourceNames) — so the
// country filter must speak the same identifier space, not raw uuids. Countries
// whose ids resolve to no known name are dropped.
export const mapDatasourcesToNames = (idMap: CountryDatasourceMap, nameById: Record<string, string>): CountryDatasourceMap => {
  const result: CountryDatasourceMap = {};
  for (const country of Object.keys(idMap) as Country_Enum[]) {
    const names = (idMap[country] ?? []).map((id) => nameById[id]).filter((name): name is string => Boolean(name));
    const distinct = [...new Set(names)];
    if (distinct.length > 0) result[country] = distinct;
  }
  return result;
};

// Pure merge of a country's datasource filter values into an existing Library
// filter payload.
// Passing `countryDatasourceIds === null` means "no country selected" and the
// filters pass through untouched. When a country is selected its datasource ids
// are intersected with any datasource ids already in the payload (AND semantics),
// so the country filter narrows — never widens — the existing toolbar selection.
export const mergeCountryDatasourceFilter = (
  filters: Record<string, unknown> | null,
  countryDatasourceIds: string[] | null
): Record<string, unknown> | null => {
  if (countryDatasourceIds === null) return filters;

  const base = filters ?? {};
  // dataSource is an EPD-datasource id list on the REST search payload.
  const existing: string[] = Array.isArray(base.dataSource) ? (base.dataSource as string[]) : [];
  const intersection = existing.length > 0 ? existing.filter((id) => countryDatasourceIds.includes(id)) : countryDatasourceIds;

  // An empty dataSource does not mean "match nothing" to the search endpoint, it means "no datasource
  // filter": omitting the key and sending [] both return the full catalogue. So an impossible
  // combination (a country plus a datasource that country does not allow) used to drop the country
  // scope and show everything, which is the opposite of narrowing. Fall back to the country's own
  // list instead, so the result never leaves the selected country's scope; the toolbar's datasource
  // choice simply cannot narrow it any further.
  const effective = intersection.length > 0 ? intersection : countryDatasourceIds;

  return { ...base, dataSource: effective };
};

// An empty list scopes nothing (see mergeCountryDatasourceFilter), so it is treated like no country.
const resolvedAllowed = (allowedNames: string[] | null): Set<string> | null =>
  allowedNames && allowedNames.length > 0 ? new Set(allowedNames) : null;

// Names of the datasource filter options the selected country does not allow; none while unresolved.
export const splitDatasourcesByAllowed = (
  items: ReadonlyArray<{ name?: string | null } | null | undefined> | null | undefined,
  allowedNames: string[] | null
): string[] => {
  const allowed = resolvedAllowed(allowedNames);
  if (!allowed) return [];
  return (items ?? []).flatMap((item) => (item?.name && !allowed.has(item.name) ? [item.name] : []));
};

// Drops datasource picks the selected country does not allow. Returns the same object when nothing
// changes; keeps an emptied group as {} so callers don't read it as a full "Clear".
export const pruneDisallowedDatasources = (selection: FilterSelection, allowedNames: string[] | null): FilterSelection => {
  const allowed = resolvedAllowed(allowedNames);
  const picks = selection?.datasources;
  if (!allowed || !picks) return selection;
  const kept = Object.fromEntries(Object.entries(picks).filter(([name]) => allowed.has(name)));
  if (Object.keys(kept).length === Object.keys(picks).length) return selection;
  return { ...selection, datasources: kept };
};

// Whether an EPD's data source(s) intersect a resolved list of allowed names — the caller resolves
// the country first (e.g. via getDatasourceFilterValues). Fails open when either side is unknown.
export const isAllowedIn = (allowedNames: string[] | undefined, datasourceNames: string[] | undefined): boolean => {
  if (!allowedNames || allowedNames.length === 0) return true;
  if (!datasourceNames || datasourceNames.length === 0) return true;
  return datasourceNames.some((name) => allowedNames.includes(name));
};
